import dayjs from '@/lib/dayjs'
import type { DiscountTargetValue } from '@/lib/enums/DiscountTarget'
import type { DiscountTypeValue } from '@/lib/enums/DiscountType'
import type { DiscountUsagePolicyValue } from '@/lib/enums/DiscountUsagePolicy'
import { z } from 'zod/v4'
import type {
  CreateDiscountInput,
  Discount,
  DiscountAvailabilityInput,
  DiscountAvailabilityValue,
  DiscountUsageInput,
  DiscountValueInput,
  UpdateDiscountInput,
} from './discounts.types'

export const DiscountTarget = {
  Product: 'product',
  Items: 'items',
  ItemsAndDelivery: 'items_and_delivery',
  Delivery: 'delivery',
} as const satisfies Record<string, DiscountTargetValue>

export const DiscountType = {
  Percent: 'percent',
  Amount: 'amount',
} as const satisfies Record<string, DiscountTypeValue>

export const DiscountUsagePolicy = {
  Unlimited: 'unlimited',
  OneTimeCode: 'one_time_code',
  LimitPerCustomer: 'limit_per_customer',
} as const satisfies Record<string, DiscountUsagePolicyValue>

export const DiscountAvailability = {
  Always: 'always',
  Range: 'range',
} as const satisfies Record<string, DiscountAvailabilityValue>

export const DEFAULT_PRODUCT_DISCOUNT_PERCENT = 10

/** The create and edit form's values. Text inputs hold what was typed until the schema coerces. */
export interface DiscountFormValues {
  name: string
  code: string
  target: DiscountTargetValue
  type: DiscountTypeValue
  value: number | string
  minimum_purchase: number | string
  usage_policy: DiscountUsagePolicyValue
  max_uses_per_customer: number | string
  is_active: boolean
  availability: DiscountAvailabilityValue
  starts_on: string
  ends_on: string
}

/** The form values the edit form can change. */
export type UpdateDiscountFormValues = Pick<
  DiscountFormValues,
  'name' | 'is_active' | 'availability' | 'starts_on' | 'ends_on'
>

const codeSchema = z
  .string()
  .trim()
  .min(3, 'Code must be at least 3 characters')
  .max(32, 'Code must be 32 characters or fewer')
  .regex(/^[A-Z0-9]+$/, 'Use letters and numbers only')

const optionalPositiveIntegerSchema = z.preprocess((value) => {
  if (value === null || value === undefined) {
    return undefined
  }

  if (typeof value === 'string' && value.trim() === '') {
    return undefined
  }

  return value
}, z.coerce.number().int().positive().optional())

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function formDateValue(value: string | null) {
  const normalizedValue = String(value ?? '').trim()

  if (!normalizedValue) {
    return ''
  }

  const parsedDate = DATE_ONLY_PATTERN.test(normalizedValue)
    ? dayjs.utc(normalizedValue, 'YYYY-MM-DD', true)
    : dayjs(normalizedValue)

  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : ''
}

export const createDiscountSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(255),
    code: codeSchema,
    target: z.enum(Object.values(DiscountTarget)),
    type: z.enum([DiscountType.Percent, DiscountType.Amount]),
    value: z.coerce.number().positive('Discount value must be greater than zero'),
    minimum_purchase: z.preprocess(
      (value) => (value === '' || value === null || value === undefined ? null : value),
      z.coerce.number().min(0).max(99999999.99).multipleOf(0.01).nullable()
    ),
    usage_policy: z.enum([
      DiscountUsagePolicy.Unlimited,
      DiscountUsagePolicy.OneTimeCode,
      DiscountUsagePolicy.LimitPerCustomer,
    ]),
    max_uses_per_customer: optionalPositiveIntegerSchema,
    is_active: z.boolean(),
    availability: z.enum([DiscountAvailability.Always, DiscountAvailability.Range]),
    starts_on: z.string().optional(),
    ends_on: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (
      value.type === DiscountType.Amount &&
      (value.minimum_purchase === null ||
        Math.round(value.minimum_purchase * 100) < Math.round(value.value * 100))
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['minimum_purchase'],
        message: 'Minimum purchase must be at least the discount amount.',
      })
    }

    if (value.type === DiscountType.Percent && value.value > 100) {
      ctx.addIssue({
        code: 'custom',
        path: ['value'],
        message: 'Discount percent cannot be greater than 100%.',
      })
    }

    if (value.usage_policy === DiscountUsagePolicy.LimitPerCustomer) {
      if (!Number.isFinite(Number(value.max_uses_per_customer))) {
        ctx.addIssue({
          code: 'custom',
          path: ['max_uses_per_customer'],
          message: 'Enter the customer use limit.',
        })
      }
    }

    if (value.availability === DiscountAvailability.Range) {
      if (!value.starts_on) {
        ctx.addIssue({ code: 'custom', path: ['starts_on'], message: 'Start date is required.' })
      }

      if (!value.ends_on) {
        ctx.addIssue({ code: 'custom', path: ['ends_on'], message: 'End date is required.' })
      }

      if (
        value.starts_on &&
        value.ends_on &&
        dayjs(value.ends_on).isBefore(dayjs(value.starts_on))
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['ends_on'],
          message: 'End date must be on or after the start date.',
        })
      }
    }
  })

export const updateDiscountSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(255),
    is_active: z.boolean(),
    availability: z.enum([DiscountAvailability.Always, DiscountAvailability.Range]),
    starts_on: z.string().optional(),
    ends_on: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.availability !== DiscountAvailability.Range) {
      return
    }

    if (!value.starts_on) {
      ctx.addIssue({ code: 'custom', path: ['starts_on'], message: 'Start date is required.' })
    }

    if (!value.ends_on) {
      ctx.addIssue({ code: 'custom', path: ['ends_on'], message: 'End date is required.' })
    }

    if (value.starts_on && value.ends_on && dayjs(value.ends_on).isBefore(dayjs(value.starts_on))) {
      ctx.addIssue({
        code: 'custom',
        path: ['ends_on'],
        message: 'End date must be on or after the start date.',
      })
    }
  })

type CreateDiscountFormOutput = z.output<typeof createDiscountSchema>

type DiscountAvailabilityFormOutput = Pick<
  CreateDiscountFormOutput,
  'availability' | 'starts_on' | 'ends_on'
>

/** The schemas' refinements reject a missing conditional field before a payload is built. */
function refinedValue<T>(value: T | null | undefined, field: string): T {
  if (value === null || value === undefined) {
    throw new Error(`The discount schema accepted a discount without ${field}.`)
  }

  return value
}

function discountValueInput({
  type,
  value,
  minimum_purchase,
}: CreateDiscountFormOutput): DiscountValueInput {
  if (type === DiscountType.Amount) {
    return { type, value, minimum_purchase: refinedValue(minimum_purchase, 'minimum_purchase') }
  }

  return { type, value, minimum_purchase }
}

function discountUsageInput({
  usage_policy,
  max_uses_per_customer,
}: CreateDiscountFormOutput): DiscountUsageInput {
  if (usage_policy === DiscountUsagePolicy.LimitPerCustomer) {
    return {
      usage_policy,
      max_uses_per_customer: refinedValue(max_uses_per_customer, 'max_uses_per_customer'),
    }
  }

  return { usage_policy, max_uses_per_customer: null }
}

function discountAvailabilityInput({
  availability,
  starts_on,
  ends_on,
}: DiscountAvailabilityFormOutput): DiscountAvailabilityInput {
  if (availability === DiscountAvailability.Range) {
    return {
      availability,
      starts_on: refinedValue(starts_on, 'starts_on'),
      ends_on: refinedValue(ends_on, 'ends_on'),
    }
  }

  return { availability, starts_on: null, ends_on: null }
}

export function defaultDiscountFormValues(): DiscountFormValues {
  return {
    name: '',
    code: '',
    target: DiscountTarget.Items,
    type: DiscountType.Percent,
    value: DEFAULT_PRODUCT_DISCOUNT_PERCENT,
    minimum_purchase: '',
    usage_policy: DiscountUsagePolicy.Unlimited,
    max_uses_per_customer: 1,
    is_active: true,
    availability: DiscountAvailability.Always,
    starts_on: '',
    ends_on: '',
  }
}

/** Route search params parse numeric codes as numbers. */
export function normalizeCodeInput(value: string | number | null | undefined): string {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

export function buildCreateDiscountPayload(values: DiscountFormValues): CreateDiscountInput {
  const parsed = createDiscountSchema.parse({
    ...values,
    code: normalizeCodeInput(values.code),
  })

  return {
    name: parsed.name,
    code: normalizeCodeInput(parsed.code),
    target: parsed.target,
    ...discountValueInput(parsed),
    ...discountUsageInput(parsed),
    is_active: parsed.is_active,
    ...discountAvailabilityInput(parsed),
  }
}

export function buildUpdateDiscountPayload(values: UpdateDiscountFormValues): UpdateDiscountInput {
  const parsed = updateDiscountSchema.parse(values)

  return {
    name: parsed.name,
    is_active: parsed.is_active,
    ...discountAvailabilityInput(parsed),
  }
}

export function formValuesFromDiscount(discount: Discount): DiscountFormValues {
  const startsOn = formDateValue(discount.starts_on ?? discount.starts_at)
  const endsOn = formDateValue(discount.ends_on ?? discount.ends_at)
  const hasRange = Boolean(startsOn && endsOn)

  return {
    ...defaultDiscountFormValues(),
    name: discount.name,
    code: discount.code ?? '',
    target: discount.target,
    type: discount.type,
    value: discount.value,
    minimum_purchase: discount.minimum_purchase ?? '',
    usage_policy: discount.usage_policy,
    max_uses_per_customer: discount.max_uses_per_customer ?? 1,
    is_active: discount.is_active,
    availability: hasRange ? DiscountAvailability.Range : DiscountAvailability.Always,
    starts_on: hasRange ? startsOn : '',
    ends_on: hasRange ? endsOn : '',
  }
}
